import { Deferred, Head, Link, router } from "@inertiajs/react"
import { ChevronDown, Play } from "lucide-react"

import { SegmentHeader } from "@/components/segment-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { VsMatchup } from "@/components/vs-matchup"
import AppLayout from "@/layouts/app-layout"
import { leaguePath, replayLeagueMatchPath } from "@/routes"
import type { BreadcrumbItem, MatchPageProps } from "@/types"

import { BroadcastFrame } from "./broadcast-frame"
import { BroadcastScoreboard } from "./broadcast-scoreboard"
import { BroadcastSummary } from "./broadcast-summary"
import { InProgressState } from "./in-progress-state"
import { KeyPlaysStorytelling } from "./key-plays/key-plays-storytelling"
import { LineupSelector } from "./lineup-selector"
import { RosterTable } from "./match-roster-table"
import { PostMatchDesk } from "./post-match-desk"

// ── Original VsScoreboard (non-completed states) ──────────────────────

function VsScoreboard({
  match,
  userTeamId,
}: {
  match: MatchPageProps["match"]
  userTeamId: string | null
}) {
  return (
    <Card className="py-0">
      <CardContent className="px-6 py-8">
        <VsMatchup
          home={{
            name: match.home_team.name,
            race: match.home_team.race,
            colorHue: match.home_team.color_hue,
            teamValue: match.home_team.team_value,
            isUser: userTeamId === match.home_team.id,
            coachName: match.home_team.coach_name,
          }}
          away={{
            name: match.away_team.name,
            race: match.away_team.race,
            colorHue: match.away_team.color_hue,
            teamValue: match.away_team.team_value,
            isUser: userTeamId === match.away_team.id,
            coachName: match.away_team.coach_name,
          }}
        />
      </CardContent>
    </Card>
  )
}

// ── Recap skeleton (shown while the deferred `recap` prop loads) ──────

function RecapSkeleton() {
  return (
    <div
      className="mx-auto max-w-4xl space-y-4 px-4 py-10"
      aria-hidden="true"
      data-testid="recap-skeleton"
    >
      <Skeleton className="mx-auto h-6 w-40" />
      <Skeleton className="h-24 w-full rounded-lg" />
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  )
}

// ── Main Page Component ───────────────────────────────────────────────

export default function MatchShow({
  league,
  match,
  round,
  season,
  user_team,
  user_roster = [],
  opponent_roster = [],
  is_home = false,
  current_lineup_ids = [],
  lineup_submitted = false,
  match_stats,
  mvps,
  spp_earned,
  injuries,
  recap,
}: MatchPageProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: league.name,
      href: leaguePath(league.id),
      leagueSwitcher: true,
    },
    { title: `Round ${round.number} · Match` },
  ]

  const userTeamId = user_team?.id ?? null
  const isUpcoming = match.status === "scheduled"
  const isInProgress = match.status === "in_progress"
  const isCompleted = match.status === "completed"

  // ── Completed: broadcast layout ─────────────────────────────────────
  if (isCompleted && match_stats) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head
          title={`${match.home_team.name} vs ${match.away_team.name} · ${league.name}`}
        />

        <BroadcastFrame>
          {/* Section 0: Broadcast Open */}
          <div className="mx-auto max-w-4xl px-4 pt-8">
            {/* Broadcast label */}
            <div className="text-center">
              <SegmentHeader
                rules={false}
                as="p"
                className="text-sm"
                label={`Season ${season.number} · Round ${round.number}`}
              />
            </div>

            {/* Scoreboard — floating, no border */}
            <BroadcastScoreboard match={match} userTeamId={userTeamId} />

            {/* Watch Replay */}
            <div className="flex justify-center pb-6">
              <Button
                variant="gold"
                className="h-11 px-6 text-lg"
                onClick={() =>
                  router.visit(replayLeagueMatchPath(league.id, match.id))
                }
              >
                <Play className="mr-2 size-5" fill="currentColor" />
                Watch Replay
              </Button>
            </div>
          </div>

          {/* Sections 1–3 (recap) ship deferred — see MatchesController#show. The
              <Deferred> boundary shows a skeleton until the partial reload delivers it. */}
          <Deferred data="recap" fallback={<RecapSkeleton />}>
            <>
              {/* Section 1: The Call — commentator summary */}
              {recap && <BroadcastSummary recap={recap} />}

              {/* Section 2: Key Plays */}
              {recap && recap.key_plays.length > 0 && (
                <div className="flex flex-col items-center py-10">
                  <SegmentHeader
                    rules={false}
                    className="text-3xl"
                    label="Key Plays"
                  />
                  <ChevronDown
                    className="mt-3 size-5 animate-bounce"
                    style={{ color: "var(--accent)" }}
                  />
                </div>
              )}
              {recap && recap.key_plays.length > 0 && (
                <KeyPlaysStorytelling
                  recap={recap}
                  homeTeamId={match.home_team.id}
                  homeTeamHue={match.home_team.color_hue ?? 55}
                  awayTeamHue={match.away_team.color_hue ?? 280}
                />
              )}

              {/* Section 3: Post-Match Desk */}
              {recap && (
                <PostMatchDesk
                  stats={match_stats}
                  match={match}
                  mvps={mvps ?? []}
                  injuries={injuries ?? []}
                  sppEarned={spp_earned ?? []}
                  recap={recap}
                  replayHref={replayLeagueMatchPath(league.id, match.id)}
                  backHref={leaguePath(league.id)}
                  leagueName={league.name}
                  leagueId={league.id}
                />
              )}
            </>
          </Deferred>
        </BroadcastFrame>
      </AppLayout>
    )
  }

  // ── Non-completed states: unchanged layout ──────────────────────────
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head
        title={`${match.home_team.name} vs ${match.away_team.name} · ${league.name}`}
      />

      <div className="mx-auto w-full max-w-7xl space-y-10 px-4 py-8">
        {/* Page header */}
        <div>
          <p className="text-muted-foreground mb-1 text-sm">
            Season {season.number} · Round {round.number}
          </p>
          <h1 className="text-2xl font-semibold tracking-tight">Match Day</h1>
        </div>

        {/* VS Scoreboard */}
        <VsScoreboard match={match} userTeamId={userTeamId} />

        {/* In-progress state */}
        {isInProgress && (
          <InProgressState
            match={match}
            lineupSize={
              user_team && current_lineup_ids.length > 0
                ? current_lineup_ids.length
                : null
            }
          />
        )}

        {/* Upcoming: lineup management + opponent roster */}
        {isUpcoming && user_team && (
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              {lineup_submitted ? (
                <RosterTable
                  roster={user_roster}
                  teamName={user_team.name}
                  leagueId={league.id}
                  teamId={user_team.id}
                />
              ) : (
                <LineupSelector
                  leagueId={league.id}
                  matchId={match.id}
                  userTeamId={user_team.id}
                  userRoster={user_roster}
                  currentLineupIds={current_lineup_ids}
                />
              )}
            </div>

            {opponent_roster.length > 0 && (
              <div>
                <RosterTable
                  roster={opponent_roster}
                  teamName={
                    is_home ? match.away_team.name : match.home_team.name
                  }
                  leagueId={league.id}
                  teamId={is_home ? match.away_team.id : match.home_team.id}
                />
              </div>
            )}
          </div>
        )}

        {/* Non-coach view for upcoming match */}
        {isUpcoming && !user_team && (
          <Card>
            <CardContent className="py-6">
              <p className="text-muted-foreground text-center text-sm">
                This match is scheduled.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Back link (for non-completed states) */}
        <div className="mt-10 text-center">
          <Link
            href={leaguePath(league.id)}
            className="text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            &larr; Back to {league.name}
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
